const PYBE_DEFAULT = 'http://127.0.0.1:8000';

/** PyBE base URL — auth, projects, and Automate APIs. */
export function getAutomateApiUrl() {
  const url = process.env.REACT_APP_AUTOMATE_API_URL
    || process.env.REACT_APP_PYBE_URL
    || PYBE_DEFAULT;
  return url.replace(/\/$/, '');
}

/**
 * Paths owned by PyBE's auth module (app/api/v1/auth.py) where a 401 means
 * "wrong credentials / bad OTP", not an expired session — excluded from the
 * Automate axios interceptor's auto Bearer-injection and 401-redirect (see
 * installAutomateHttpInterceptors). /api/auth/me and /api/auth/profile/photo
 * are intentionally NOT listed here: a 401 there is a genuinely expired
 * session and should flow through the normal interceptor (Bearer injection +
 * redirect to /login), same as any other authenticated PyBE route.
 */
const AUTH_OWNED_PATH_RE = /\/api\/auth\/(register|verify-otp|resend-otp)(\/|\?|$)/;

export function isAuthOwnedPath(url) {
  return AUTH_OWNED_PATH_RE.test(String(url || ''));
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Automate sessions use UUID ids from PyBE; Hub requests use numeric ids. */
export function isAutomateRequestId(requestId) {
  return UUID_RE.test(String(requestId || '').trim());
}

/** Drop stale Hub numeric id saved before the PyBE split. */
export function clearStoredAutomateRequestId(projectId) {
  const pid = projectId || 'all';
  const key = `ri-pending-confirm-${pid}`;
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

/** PyBE Automate API path — null when id is a legacy Hub numeric request. */
export function automateRequestPath(requestId, suffix = '') {
  if (!isAutomateRequestId(requestId)) return null;
  const id = String(requestId).trim();
  return `${getAutomateApiUrl()}/api/requests/${id}${suffix}`;
}

export const LEGACY_AUTOMATE_ID_MESSAGE =
  'This Requirement Intake session moved to PyBE. Start a new flow — legacy Hub request ids are no longer supported.';

export function getAuthToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

/** Model tiers exposed by the shared IXDev gateway — same set across the whole app. */
export const MODEL_TIERS = [
  { value: 'ixdi/auto', label: 'Auto', description: 'Automatically picks a suitable model per task (recommended default)' },
  { value: 'ixdi/fast', label: 'Fast', description: 'Quick, low-cost — best for small/simple content' },
  { value: 'ixdi/deep', label: 'Deep', description: 'Extended reasoning for hard problems (experimental)' },
  { value: 'ixdi/secure', label: 'Secure', description: 'Most reliable for large or complex content — specs, test generation' },
];
export const DEFAULT_MODEL_TIER = 'ixdi/auto';
const MODEL_TIER_STORAGE_KEY = 'continuum_model_tier';

/** App-wide model tier selection (top-header dropdown) — persisted so it applies across pages/reloads. */
export function getModelTier() {
  const stored = localStorage.getItem(MODEL_TIER_STORAGE_KEY);
  return MODEL_TIERS.some((t) => t.value === stored) ? stored : DEFAULT_MODEL_TIER;
}

export function setModelTier(tier) {
  const value = MODEL_TIERS.some((t) => t.value === tier) ? tier : DEFAULT_MODEL_TIER;
  localStorage.setItem(MODEL_TIER_STORAGE_KEY, value);
  return value;
}

export function authHeaders(extra = {}) {
  const token = getAuthToken();
  const headers = { 'X-Model-Tier': getModelTier(), ...extra };
  if (!token) return headers;
  return { Authorization: `Bearer ${token}`, ...headers };
}
